import type { VideoJsPlayer } from 'video.js';
import type { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

const SEEK_TIMEOUT_MS = 400;

type PlaybackContext = IAssetViewerStateContextValue;

type TimeRange = {
  start: number;
  end: number | null;
};

export const isAudioVideoType = (type?: string): boolean =>
  type === 'audio' || type === 'video';

function getPlayer(context: PlaybackContext): VideoJsPlayer | null {
  return context.getPlaybackPlayer();
}

function safelyRead<T>(
  player: VideoJsPlayer | null,
  read: (player: VideoJsPlayer) => T
): T | null {
  if (!player) return null;
  try {
    return read(player);
  } catch {
    return null;
  }
}

function safelyRun(
  player: VideoJsPlayer | null,
  action: (player: VideoJsPlayer) => void
): void {
  if (!player) return;
  try {
    action(player);
  } catch {
    // player disposed
  }
}

function getCurrentTime(player: VideoJsPlayer | null): number | null {
  return safelyRead(player, (p) => {
    const time = p.currentTime();
    return Number.isFinite(time) ? time : null;
  });
}

function getTimeRange(context: PlaybackContext): TimeRange | null {
  const [start, end = null] = context.playerTime ?? [];
  if (start == null || !Number.isFinite(start)) return null;
  return { start, end };
}

function waitForSeek(player: VideoJsPlayer | null): Promise<void> {
  if (!player) return Promise.resolve();

  return new Promise((resolve) => {
    let completed = false;
    const finish = () => {
      if (completed) return;
      completed = true;
      clearTimeout(timeoutId);
      try {
        player.off('seeked', onSeeked);
      } catch {
        // ignore
      }
      resolve();
    };
    const onSeeked = () => finish();
    const timeoutId = setTimeout(finish, SEEK_TIMEOUT_MS);
    try {
      player.on('seeked', onSeeked);
    } catch {
      finish();
    }
  });
}

async function syncPlayers(
  left: PlaybackContext,
  right: PlaybackContext,
  range: TimeRange
): Promise<void> {
  const leftPlayer = getPlayer(left);
  const rightPlayer = getPlayer(right);

  safelyRun(leftPlayer, (p) => p.pause());
  safelyRun(rightPlayer, (p) => p.pause());

  left.setPlayerTime([range.start, range.end]);
  right.setPlayerTime([range.start, range.end]);

  safelyRun(leftPlayer, (p) => p.currentTime(range.start));
  safelyRun(rightPlayer, (p) => p.currentTime(range.start));

  await Promise.all([waitForSeek(leftPlayer), waitForSeek(rightPlayer)]);

  requestAnimationFrame(() => {
    safelyRun(leftPlayer, (p) => p.play()?.catch(() => undefined));
    safelyRun(rightPlayer, (p) => p.play()?.catch(() => undefined));
  });
}

export function alignComparePlayerChromeFromLeft(
  left: PlaybackContext,
  right: PlaybackContext
): void {
  const leftPlayer = getPlayer(left);
  const rightPlayer = getPlayer(right);
  if (!leftPlayer || !rightPlayer) return;
  try {
    rightPlayer.volume(leftPlayer.volume());
    rightPlayer.muted(leftPlayer.muted());
    rightPlayer.playbackRate(leftPlayer.playbackRate());
  } catch {
    // ignore
  }
}

export async function syncCompareRightFromLeft(
  left: PlaybackContext,
  right: PlaybackContext
): Promise<void> {
  const leftPlayer = getPlayer(left);
  const timeRange = getTimeRange(left);
  const currentTime = getCurrentTime(leftPlayer) ?? timeRange?.start;

  if (currentTime == null) return;

  await syncPlayers(left, right, {
    start: Math.max(0, currentTime),
    end: timeRange?.end ?? null
  });
}
